import React from 'react';
import { logDebug, logError } from 'utils/logger';
import Button from 'components/Button';
import Modal from 'components/Modal';
import { api } from 'api/api';
import styled from 'styled-components';
import { useTeamColors } from 'hooks';
import { safeLogEvent } from 'firebase.js';
import { getLeagueSport } from 'components/Selector/leagueToSport';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 400px;
  padding: 48px 12px 12px 12px;
  margin: 0;
`;

const ChatHistory = styled.div`
  overflow-y: auto;
  flex-grow: 1;
  margin-bottom: 12px;
  padding: 16px;
  border-radius: 12px;
  background: #1a1a1a;
  min-height: 0;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  display: ${(props) => (props.$show ? 'block' : 'none')};
  animation: ${(props) => (props.$show ? 'fadeIn 0.3s ease-in' : 'none')};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`;

const Message = styled.div`
  margin-bottom: 24px;
  line-height: 1.4;
  color: #ffffff !important;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  &.user {
    margin-bottom: 16px;
    color: #ffffff !important;
  }

  &.assistant {
    margin-bottom: 32px;
    color: #ffffff !important;
  }

  &.loading {
    margin-bottom: 16px;
    color: rgba(255, 255, 255, 0.6) !important;
    font-style: italic;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid ${(props) => props.$color || 'rgba(255, 255, 255, 0.3)'};
  border-top-color: ${(props) => props.$topColor || '#ffffff'};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 80px;
  margin-top: auto;
  padding-top: 8px;
`;

const InputWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  height: 60px;
  display: flex;
  align-items: center;
`;

const Input = styled.textarea.attrs({
  'data-testid': 'game-web-search-input',
})`
  flex-grow: 1;
  background: rgba(0, 0, 0, 0.9);
  border: none;
  border-radius: 12px;
  padding: 12px 32px 12px 12px;
  color: #ffffff !important;
  font-size: 14px;
  resize: none;
  height: 100%;
  width: 100%;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  &:focus {
    outline: none;
    color: #ffffff !important;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #808080;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 2px;
    background: black;
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`;

const processMarkdown = (text) => {
  if (!text) return null;
  return text.split(/(\*\*.*?\*\*)/).map((part, index) => {
    const key = `${part.replace(/[^a-zA-Z0-9]/g, '')}-${index}`;
    if (part.startsWith('**') && part.endsWith('**')) {
      const content = part.slice(2, -2);
      return <strong key={key}>{content}</strong>;
    }
    return <span key={key}>{part}</span>;
  });
};

const questionTemplates = [
  (team, opponent, league) => {
    const sport = getLeagueSport(league);
    const sportContext = sport ? ` in ${sport}` : '';
    const leagueContext = league ? ` (${league})` : '';

    // Prioritize game-specific questions when both teams are available
    if (team && opponent && league) {
      return `What was the score the last time ${team} played ${opponent}${sportContext}${leagueContext}?`;
    }
    if (team && opponent) {
      return `What was the score the last time ${team} played ${opponent}?`;
    }
    // Team-specific question when only team is available
    if (team && league) {
      return `What are ${team}'s upcoming games in ${league}?`;
    }
    if (team) {
      return `What are ${team}'s upcoming games?`;
    }
    // League-specific fallback
    if (league) {
      return `What are the top games happening in ${league} this week?`;
    }
    // Only show if we have no context at all
    return null;
  },
  (team, opponent, league) => {
    const sport = getLeagueSport(league);
    const sportContext = sport ? ` ${sport}` : '';

    // Game-specific: team record
    if (team && league) {
      return `What is ${team}'s${sportContext} record this season in ${league}?`;
    }
    if (team) {
      return `What is ${team}'s record this season?`;
    }
    // League-specific fallback
    if (league) {
      return `Who are the top teams in ${league} right now?`;
    }
    return null;
  },
  (team, opponent, league) => {
    const sport = getLeagueSport(league);
    const sportContext = sport ? ` ${sport}` : '';

    // Game-specific: opponent record
    if (opponent && league) {
      return `What is ${opponent}'s${sportContext} record this season in ${league}?`;
    }
    if (opponent) {
      return `What is ${opponent}'s record this season?`;
    }
    // League standings fallback
    if (league) {
      return `What are the current standings in ${league}?`;
    }
    return null;
  },
  (team, opponent, league) => {
    const sport = getLeagueSport(league);
    const sportContext = sport ? ` ${sport}` : '';
    const leagueContext = league ? ` in ${league}` : '';

    // Game-specific: key players for both teams
    if (team && opponent && league) {
      return `Who are the key${sportContext} players to watch in the ${team} vs ${opponent} game${leagueContext}?`;
    }
    if (team && opponent) {
      return `Who are the key players to watch in the ${team} vs ${opponent} game?`;
    }
    // Team-specific: key players
    if (team && league) {
      return `Who are the key${sportContext} players to watch for ${team}${leagueContext}?`;
    }
    if (team) {
      return `Who are the key players to watch for ${team}?`;
    }
    if (opponent && league) {
      return `Who are the key${sportContext} players to watch for ${opponent}${leagueContext}?`;
    }
    if (opponent) {
      return `Who are the key players to watch for ${opponent}?`;
    }
    // League-specific fallback
    if (league) {
      return `Who are the top players in ${league} right now?`;
    }
    return null;
  },
  (team, opponent, league) => {
    const sport = getLeagueSport(league);
    const sportContext = sport ? ` ${sport}` : '';
    const leagueContext = league ? ` in ${league}` : '';

    // Game-specific: injury reports for both teams
    if (team && opponent && league) {
      return `What are the${sportContext} injury reports for ${team} and ${opponent}${leagueContext}?`;
    }
    if (team && opponent) {
      return `What are the injury reports for ${team} and ${opponent}?`;
    }
    // Team-specific: injury reports
    if (team && league) {
      return `What are the${sportContext} injury reports for ${team}${leagueContext}?`;
    }
    if (team) {
      return `What are the injury reports for ${team}?`;
    }
    if (opponent && league) {
      return `What are the${sportContext} injury reports for ${opponent}${leagueContext}?`;
    }
    if (opponent) {
      return `What are the injury reports for ${opponent}?`;
    }
    // League-specific fallback
    if (league) {
      return `What are the latest injury reports in ${league}?`;
    }
    return null;
  },
  (team, opponent, league) => {
    const sport = getLeagueSport(league);
    const sportContext = sport ? ` in ${sport}` : '';
    const leagueContext = league ? ` in ${league}` : '';

    // Game-specific: head-to-head record
    if (team && opponent && league) {
      return `What is the head-to-head${sportContext} record between ${team} and ${opponent}${leagueContext}?`;
    }
    if (team && opponent) {
      return `What is the head-to-head record between ${team} and ${opponent}?`;
    }
    // Team-specific: recent performance
    if (team && league) {
      return `How has ${team} performed recently in ${league}?`;
    }
    if (team) {
      return `How has ${team} performed recently?`;
    }
    if (opponent && league) {
      return `How has ${opponent} performed recently in ${league}?`;
    }
    if (opponent) {
      return `How has ${opponent} performed recently?`;
    }
    // League-specific fallback
    if (league) {
      return `What are the most anticipated matchups in ${league} this season?`;
    }
    return null;
  },
];

// In-memory conversation cache per event
const conversationCache = new Map();
// Track question index per event
const questionIndexCache = new Map();

// Cache version - increment this to invalidate all old cached questions
// Version 2: Added sport/league context to questions
const CACHE_VERSION = 2;

// Generate unique key for each event (includes cache version)
const getEventKey = (team, opponent, league) => {
  // Try to get league from URL if not provided
  if (!league || league === 'unknown') {
    const urlParams = new URLSearchParams(window.location.search);
    // Check both 'league' and 'collegeLeague' parameters (college sites use collegeLeague)
    league =
      urlParams.get('league') || urlParams.get('collegeLeague') || 'unknown';
  }
  return `v${CACHE_VERSION}_${team || 'unknown'}_vs_${opponent || 'unknown'}_${league || 'unknown'}`;
};

// Rate limiting constants
const RATE_LIMIT_KEY = 'ai_search_rate_limit';
const MAX_QUERIES_PER_DAY = 5;
const RATE_LIMIT_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

// Rate limiting helper functions
const getRateLimitData = () => {
  try {
    const data = localStorage.getItem(RATE_LIMIT_KEY);
    if (!data)
      return { count: 0, resetTime: Date.now() + RATE_LIMIT_WINDOW_MS };
    return JSON.parse(data);
  } catch (error) {
    logError('Error reading rate limit data', error);
    return { count: 0, resetTime: Date.now() + RATE_LIMIT_WINDOW_MS };
  }
};

const setRateLimitData = (data) => {
  try {
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data));
  } catch (error) {
    logError('Error saving rate limit data', error);
  }
};

const checkRateLimit = () => {
  const data = getRateLimitData();
  const now = Date.now();

  // Reset if window has passed
  if (now >= data.resetTime) {
    const newData = { count: 0, resetTime: now + RATE_LIMIT_WINDOW_MS };
    setRateLimitData(newData);
    return {
      allowed: true,
      remaining: MAX_QUERIES_PER_DAY,
      resetTime: newData.resetTime,
    };
  }

  // Check if under limit
  const allowed = data.count < MAX_QUERIES_PER_DAY;
  const remaining = Math.max(0, MAX_QUERIES_PER_DAY - data.count);

  return { allowed, remaining, resetTime: data.resetTime };
};

const incrementRateLimit = () => {
  const data = getRateLimitData();
  const now = Date.now();

  // Reset if window has passed
  if (now >= data.resetTime) {
    const newData = { count: 1, resetTime: now + RATE_LIMIT_WINDOW_MS };
    setRateLimitData(newData);
    return;
  }

  // Increment count
  data.count += 1;
  setRateLimitData(data);
};

const GameWebSearch = ({ eventLeagueName, onClose, opponent, show, team }) => {
  const { mainColor } = useTeamColors();

  // Debug logging
  React.useEffect(() => {
    // Get league from URL as fallback (check both 'league' and 'collegeLeague')
    const urlParams = new URLSearchParams(window.location.search);
    const leagueFromUrl =
      urlParams.get('league') || urlParams.get('collegeLeague');
    const effectiveLeague = eventLeagueName || leagueFromUrl;

    logDebug('[GameWebSearch] Props received:', {
      team,
      opponent,
      eventLeagueName,
      leagueFromUrl,
      effectiveLeague,
      sport: getLeagueSport(effectiveLeague),
    });
  }, [team, opponent, eventLeagueName]);

  const eventKey = React.useMemo(
    () => getEventKey(team, opponent, eventLeagueName),
    [team, opponent, eventLeagueName]
  );

  const [messages, setMessages] = React.useState(
    // Load from cache on mount
    () => conversationCache.get(eventKey) || []
  );
  const [inputText, setInputText] = React.useState('');
  const chatHistoryRef = React.useRef(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(
    () => questionIndexCache.get(eventKey) || 0
  );
  const [isLoading, setIsLoading] = React.useState(false);

  const generateResponse = React.useCallback(async () => {
    if (!inputText?.trim() || isLoading) return;

    // Check rate limit
    const rateLimitCheck = checkRateLimit();
    if (!rateLimitCheck.allowed) {
      const resetDate = new Date(rateLimitCheck.resetTime);
      const resetTimeStr = resetDate.toLocaleString();

      safeLogEvent('ai_search_rate_limit_hit', {
        team,
        opponent,
        league: eventLeagueName,
        reset_time: resetTimeStr,
      });

      const errorMessage = {
        id: `error-${Date.now()}`,
        text: `**Trial Mode Limit Reached**\n\nYou've used all ${MAX_QUERIES_PER_DAY} AI search queries for today. This feature is currently in trial mode with limited usage.\n\nYour limit will reset at: ${resetTimeStr}\n\nCome back tomorrow to continue exploring!`,
        type: 'error',
      };
      setMessages((prev) => [...prev, errorMessage]);
      return;
    }

    setIsLoading(true);

    try {
      const userMessage = {
        id: `user-${Date.now()}`,
        text: inputText,
        type: 'user',
      };
      setMessages((prev) => [...prev, userMessage]);

      // Add loading message
      const loadingMessage = {
        id: `loading-${Date.now()}`,
        text: 'Please wait...',
        type: 'loading',
      };
      setMessages((prev) => [...prev, loadingMessage]);

      logDebug('Requesting AI game search...');

      // Track AI search prompt
      safeLogEvent('ai_search_prompt_sent', {
        team,
        opponent,
        league: eventLeagueName,
        query_preview: inputText.substring(0, 100),
        query_length: inputText.length,
        remaining_queries: rateLimitCheck.remaining - 1,
      });

      // The API adds the Perplexity key server-side (AiGameSearchController
      // in downkount-api-main) - it is never shipped to the browser.
      const { data } = await api.ai.gameSearch({
        query: inputText.trim(),
        team,
        opponent,
        league: eventLeagueName,
      });
      const text = data?.text;

      logDebug('Response received:', text?.substring(0, 100));

      // Increment rate limit after successful response
      incrementRateLimit();

      // Strip citation markers like [1], [2], [3] from the response
      const cleanedText = text?.replace(/\[\d+\]/g, '') || text;

      // Track successful response
      safeLogEvent('ai_search_response_received', {
        team,
        opponent,
        league: eventLeagueName,
        response_length: cleanedText?.length || 0,
        remaining_queries: rateLimitCheck.remaining - 1,
      });

      // Remove loading message and add AI response
      setMessages((prev) => prev.filter((msg) => msg.type !== 'loading'));

      const aiMessage = {
        id: `ai-${Date.now()}`,
        text: cleanedText,
        type: 'assistant',
      };
      setMessages((prev) => [...prev, aiMessage]);

      // Increment question index and load next question
      setCurrentQuestionIndex((prev) => {
        const nextIndex = prev + 1;
        // Calculate next question based on the new index
        let attempts = 0;
        let index = nextIndex;

        while (attempts < questionTemplates.length) {
          if (index >= questionTemplates.length) {
            index = 0;
          }

          const question = questionTemplates[index](
            team,
            opponent,
            eventLeagueName
          );

          if (question) {
            setInputText(question);
            return nextIndex;
          }

          index++;
          attempts++;
        }

        // Fallback
        setInputText('Ask a question about the event...');
        return nextIndex;
      });

      setIsLoading(false);
    } catch (error) {
      const rateLimited = error?.response?.status === 429;

      logError('Error generating AI response', error, {
        inputText: inputText?.substring(0, 50),
      });

      // Track AI search error
      safeLogEvent('ai_search_error', {
        error_type: rateLimited ? 'rate_limited_server' : 'generation_failed',
        error_message: error?.message || 'Unknown error',
        team,
        opponent,
        league: eventLeagueName,
        query_preview: inputText?.substring(0, 100),
      });

      // Remove loading message
      setMessages((prev) => prev.filter((msg) => msg.type !== 'loading'));

      const errorMessage = {
        id: `error-${Date.now()}`,
        text: rateLimited
          ? `**Trial Mode Limit Reached**\n\nAI search is limited while in trial mode. Please try again later.`
          : 'Sorry, there was an error generating a response. Please try again.',
        type: 'error',
      };
      setMessages((prev) => [...prev, errorMessage]);

      setIsLoading(false);
    }
  }, [inputText, team, opponent, eventLeagueName, isLoading]);

  const getNextQuestion = React.useCallback(() => {
    // Try to find a valid question starting from current index
    let attempts = 0;
    let index = currentQuestionIndex;

    while (attempts < questionTemplates.length) {
      if (index >= questionTemplates.length) {
        index = 0; // Cycle back to start
      }

      const question = questionTemplates[index](
        team,
        opponent,
        eventLeagueName
      );

      // Debug logging
      if (question) {
        logDebug('[GameWebSearch] Generated question:', {
          templateIndex: index,
          question,
          team,
          opponent,
          eventLeagueName,
          sport: getLeagueSport(eventLeagueName),
        });
      }

      // If we found a valid question (not null), return it
      if (question) {
        return question;
      }

      // Try next template
      index++;
      attempts++;
    }

    // Fallback if no valid questions found (shouldn't happen if we have any data)
    return 'Ask a question about the event...';
  }, [currentQuestionIndex, eventLeagueName, opponent, team]);

  React.useEffect(() => {
    // Set question when event changes or on mount
    const initialQuestion = getNextQuestion();
    setInputText(initialQuestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventKey]); // Run when event changes

  const scrollToBottom = React.useCallback(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, []);

  React.useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Save conversation to cache whenever messages change
  React.useEffect(() => {
    if (messages.length > 0) {
      conversationCache.set(eventKey, messages);
    }
  }, [messages, eventKey]);

  // Save question index to cache whenever it changes
  React.useEffect(() => {
    questionIndexCache.set(eventKey, currentQuestionIndex);
  }, [currentQuestionIndex, eventKey]);

  // Load conversation and question index when event changes
  React.useEffect(() => {
    const cachedMessages = conversationCache.get(eventKey);
    const cachedIndex = questionIndexCache.get(eventKey);

    if (cachedMessages) {
      setMessages(cachedMessages);
    } else {
      setMessages([]);
    }

    if (cachedIndex !== undefined) {
      setCurrentQuestionIndex(cachedIndex);
    } else {
      setCurrentQuestionIndex(0);
    }
  }, [eventKey]);

  return (
    <Modal onClose={onClose} show={show}>
      <ChatContainer>
        <ChatHistory $show={messages.length > 0} ref={chatHistoryRef}>
          {messages?.map((message) => (
            <Message className={message.type} key={message.id}>
              {message.type === 'loading' ? (
                <>
                  <Spinner $color={`${mainColor}33`} $topColor={mainColor} />
                  {message.text}
                </>
              ) : message.text ? (
                processMarkdown(message.text)
              ) : null}
            </Message>
          ))}
        </ChatHistory>
        <InputContainer>
          <InputWrapper>
            <Input
              maxLength={300}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void generateResponse();
                }
              }}
              placeholder="Ask a question about the event..."
              value={inputText || ''}
            />
            {inputText ? (
              <ClearButton onClick={() => setInputText('')} type="button" />
            ) : null}
          </InputWrapper>
          <Button
            disabled={isLoading || !inputText?.trim()}
            onClick={() => void generateResponse()}
            title={isLoading ? 'Sending...' : 'Send'}
          />
        </InputContainer>
      </ChatContainer>
    </Modal>
  );
};

export default GameWebSearch;
